import * as THREE from "three";
import type { Structure, Member } from "@/physics/model";
import type { BuiltTower, RenderModel } from "@/game/towerSpec";
import { groundHeight } from "@/render/terrain";

/**
 * Renders a live BuiltTower. Lattice/guyed towers draw every member with a
 * cross-section that matches its real steel shape — round pipe, L-angle, flat
 * bar, round rod — grouped into one InstancedMesh per shape family and colored
 * by utilization (green→red — the core feedback mechanic). Guys (tension-only)
 * render as drooping catenary cables. A single invisible unit-cylinder "pick
 * proxy" mesh (instanceId === member index) is exposed for click-picking so ANY
 * member — including guys — can be raycast-inspected. Monopoles hide the
 * internal lattice and draw a tapered steel tube skin following the (deforming)
 * centerline. Foundations and guy anchors are drawn as static blocks.
 */

const UP = new THREE.Vector3(0, 1, 0);
const DOWN = new THREE.Vector3(0, -1, 0);

// --- member cross-section shape families ---
// Classified from the section catalog naming (see physics/model.ts SECTIONS):
// Pipe … → round tube, L… → L-angle, Flat … → flat bar, Ø… rod → round rod.
// Guys (tension-only) are drawn as catenary cables, not by these families.
type ShapeKind = "pipe" | "angle" | "flat" | "rod";

function classifyShape(m: Member): ShapeKind {
  const n = m.section.name;
  if (n.startsWith("L")) return "angle";
  if (n.startsWith("Flat")) return "flat";
  if (n.includes("rod")) return "rod";
  return "pipe"; // "Pipe …" and any unrecognised section fall back to round tube
}

// Number of segments used to draw each drooping guy catenary.
const GUY_SEGMENTS = 8;

// Over-stretch guard: if a drawn span's current endpoint distance exceeds
// OVERSTRETCH_TRIGGER × L0 the connection has effectively parted (physics dropped
// its constraint) — the span is clamped to OVERSTRETCH_DRAW × L0 from its anchor
// end so a severed member never renders as a ~10× rubber band. Visual only.
const OVERSTRETCH_TRIGGER = 1.6;
const OVERSTRETCH_DRAW = 1.3;

// How long a snapped guy hangs straight down from its anchor: a fraction of its
// rest length, capped so it stays a short sensible droop rather than spanning to
// the (fallen) far end.
const FAILED_GUY_HANG_FRAC = 0.5;
const FAILED_GUY_HANG_CAP = 6;

// All shape families, hoisted so the per-frame update loop never allocates.
const SHAPE_KINDS: ShapeKind[] = ["pipe", "angle", "flat", "rod"];

const cGreen = new THREE.Color(0x35c46a);
const cYellow = new THREE.Color(0xe6d13a);
const cRed = new THREE.Color(0xe0402f);
const cSlack = new THREE.Color(0x5a6b82);
const cFailed = new THREE.Color(0x3a1512);
// Charred, mangled-steel dark reds for broken debris (varied per-member so the
// wreckage doesn't read as uniform). Darker/more scorched than live cFailed.
const cChar = new THREE.Color(0x24100e);
const cEmber = new THREE.Color(0x5a1a10);
const cSteel = new THREE.Color(0x9fb2c2);
// Dark galvanised steel for bolt plates / bolts / anchors — reads as steelwork
// hardware, visibly darker than the bright members so it doesn't overwhelm.
const cGalv = new THREE.Color(0x5b6570);
const _tmp = new THREE.Color();

// REALISTIC-view member steel: a mid galvanised grey. Per-member variation is
// applied on top (see steelColor) so a realistic render doesn't look like one
// flat-shaded blob. Guys/cables get a slightly cooler, darker cable grey.
const cRealSteel = new THREE.Color(0x8a97a4);
const cRealCable = new THREE.Color(0x6b7480);

// REALISTIC-view base colour per material, keyed by Material.name (names are
// unique in MATERIALS). Lets a bamboo tower render as bamboo, a spaghetti tower
// as pasta, etc. — steels stay galvanised grey. Falls back to cRealSteel.
const MAT_COLOR: Record<string, THREE.Color> = {
  "A36": cRealSteel,
  "A572 Gr50": cRealSteel,
  "A992": cRealSteel,
  "EHS strand": cRealCable,
  "Kevlar (aramid)": new THREE.Color(0xd9c98a), // aramid yellow
  "Aluminium 6061-T6": new THREE.Color(0xc7ccd2), // bright silver
  "Stainless 304": new THREE.Color(0xd4d9de), // polished
  "Titanium Ti-6Al-4V": new THREE.Color(0x9a9488), // warm grey
  "Wrought iron": new THREE.Color(0x6a6a66), // dark iron
  "Cast iron (grey)": new THREE.Color(0x53585c), // dark grey
  "Carbon fibre (CFRP)": new THREE.Color(0x2c2f35), // near-black weave
  "Fibreglass (GFRP)": new THREE.Color(0xd6ddd2), // pale green-white
  "Timber (Douglas-fir)": new THREE.Color(0xb5895a), // tan wood
  "Oak (white)": new THREE.Color(0x9c6b3f), // darker wood
  "Bamboo (Moso)": new THREE.Color(0xc7b24f), // yellow-green cane
  "Dry spaghetti 🍝": new THREE.Color(0xe6cb87), // pale wheat
  "Green willow 🌿": new THREE.Color(0x7fa64c), // fresh green
  "Ice ❄️": new THREE.Color(0xcfe6f5), // icy blue-white
  "Glass (soda-lime)": new THREE.Color(0xbfe0e6), // pale cyan
};

/** Base realistic colour for a member's material (falls back to steel/cable grey). */
function materialBase(m: Member): THREE.Color {
  const c = MAT_COLOR[m.material.name];
  if (c) return c;
  return m.tensionOnly ? cRealCable : cRealSteel;
}

// Frosted ice coating: pale icy blue-white. Rendered translucent over the steel.
const cIce = new THREE.Color(0xd6ecff);
// How much the (metres of) ice thickness is exaggerated in the sleeve radius so a
// few centimetres of ice reads clearly at tower scale. Guys get an extra factor
// on top (ice on cables is the dangerous real-world case, so make it obvious).
const ICE_EXAGGERATE = 8;
const ICE_GUY_EXTRA = 1.6;

// Deterministic per-member steel color: cRealSteel nudged ±brightness by a hash
// of the member index so adjacent members read as distinct galvanised sections
// without any per-frame allocation or Math.random.
function steelColor(idx: number, m: Member, out: THREE.Color): THREE.Color {
  if (m.failed) return out.copy(cFailed);
  if (m.slack) return out.copy(cSlack);
  const h = Math.sin(idx * 91.7 + 11.13) * 43758.5453;
  const f = h - Math.floor(h); // 0..1 stable per member
  const base = materialBase(m); // per-material colour (bamboo, pasta, steel…)
  const shade = 0.82 + f * 0.32; // 0.82..1.14 brightness jitter
  return out.copy(base).multiplyScalar(shade);
}

// A hidden instance: squashed to nothing so it doesn't render. Used to park the
// unused half of a member's debris pair (buckle uses both, snap uses both, live
// members use neither).
const HIDDEN_SCALE = 0.00001;

interface BreakParams {
  end: 0 | 1; // which endpoint the SNAP anchors more length toward (unbalanced tear)
  frac: number; // total broken length as a fraction of the member's original length
  droop: number; // 0..1 blend of debris direction toward straight-down
  kinkDir: THREE.Vector3; // unit sideways direction the buckle bows toward
  kinkAmt: number; // how far the buckle kink is displaced (× radius)
  twist: number; // roll (radians) applied to each shard so metal reads twisted
  gap: number; // snap tear gap as a fraction of length
  tint: THREE.Color; // per-member charred color
}

// Build a unit-length geometry for a shape family, aligned along +Y with its
// cross-section spanning roughly [-1,1] in X/Z so a per-instance scale of
// (r, len, r) yields a member of length `len` and cross-section ~r. Origin is
// centred on the axis so the existing midpoint-orient math is reused unchanged.
function makeShapeGeometry(kind: ShapeKind): THREE.BufferGeometry {
  switch (kind) {
    case "pipe":
      // round tube — 8-sided cylinder (was the universal member shape)
      return new THREE.CylinderGeometry(1, 1, 1, 8);
    case "rod":
      // slim solid round bar — smoother, thinner; radius trimmed via scale later
      return new THREE.CylinderGeometry(1, 1, 1, 7);
    case "flat": {
      // thin wide rectangular bar: wide in X, thin in Z, unit length in Y.
      const g = new THREE.BoxGeometry(1.7, 1, 0.32);
      return g;
    }
    case "angle": {
      // L-section: two flanges meeting at a corner. Extrude an L profile along
      // Y, then centre it on the axis so instance orient math needs no offset.
      const t = 0.34; // flange thickness (units)
      const w = 1.6; // flange length (units)
      const s = new THREE.Shape();
      s.moveTo(0, 0);
      s.lineTo(w, 0);
      s.lineTo(w, t);
      s.lineTo(t, t);
      s.lineTo(t, w);
      s.lineTo(0, w);
      s.closePath();
      const g = new THREE.ExtrudeGeometry(s, { depth: 1, bevelEnabled: false });
      // Extrude runs along +Z from 0..1: reorient so the length axis is +Y and
      // recentre the L in its cross-section plane and along its length.
      g.translate(-w * 0.32, -w * 0.32, -0.5);
      g.rotateX(-Math.PI / 2);
      return g;
    }
  }
}

function utilColor(u: number, out: THREE.Color): THREE.Color {
  const c = Math.min(1, Math.max(0, u));
  if (c < 0.6) out.copy(cGreen).lerp(cYellow, c / 0.6);
  else out.copy(cYellow).lerp(cRed, (c - 0.6) / 0.4);
  return out;
}

function memberColor(m: Member, out: THREE.Color): THREE.Color {
  if (m.failed) return out.copy(cFailed);
  if (m.slack) return out.copy(cSlack);
  return utilColor(m.util, out);
}

function visualRadius(m: Member): number {
  // Exaggerate section-size differences so heavier steel visibly reads thicker
  // (keyed off the real section width/OD). Guys stay thin cables.
  if (m.tensionOnly) return 0.03;
  return 0.03 + m.section.dragWidth * 0.9;
}

const FLAG_ORANGE = "#F47B20"; // Omnitouch orange

/** Orange flag texture with the real Omnitouch logo (public/omnitouch-flag.png)
 *  composited on. No invented text — if the file isn't loaded yet it's just orange. */
function makeFlagTexture(): THREE.CanvasTexture {
  const w = 512;
  const h = 220; // matches the logo's wide aspect
  const c = document.createElement("canvas");
  c.width = w;
  c.height = h;
  const g = c.getContext("2d")!;
  g.fillStyle = FLAG_ORANGE;
  g.fillRect(0, 0, w, h);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;

  const img = new Image();
  img.onload = () => {
    g.fillStyle = FLAG_ORANGE;
    g.fillRect(0, 0, w, h);
    const pad = 0.08;
    const s = Math.min((w * (1 - 2 * pad)) / img.width, (h * (1 - 2 * pad)) / img.height);
    const dw = img.width * s;
    const dh = img.height * s;
    g.drawImage(img, (w - dw) / 2, (h - dh) / 2, dw, dh); // the actual logo, centred
    tex.needsUpdate = true;
  };
  img.src = "/omnitouch-flag.png";
  return tex;
}

export class StructureView {
  readonly group = new THREE.Group();
  private structure: Structure;
  private render: RenderModel;

  /**
   * Invisible unit-cylinder mesh used solely for click-picking: one straight
   * instance per member with instanceId === member index, so main.ts can raycast
   * ANY member (including guys) and read the member index back. Kept raycastable
   * (material.visible=false still intersects) but never drawn.
   */
  get pickTarget(): THREE.InstancedMesh {
    return this.pickProxy;
  }

  private pickProxy: THREE.InstancedMesh;
  // Visible cross-section meshes, one InstancedMesh per shape family. Each member
  // (except guys) occupies one slot in exactly one of these; shapeOf/shapeSlot
  // map a member index → its family mesh + local instance slot.
  private shapeMeshes: Record<ShapeKind, THREE.InstancedMesh>;
  private shapeOf: (ShapeKind | null)[]; // null for guys (drawn as catenary)
  private shapeSlot: number[]; // local instance index within its family mesh
  // Per-member stable roll (radians) so angles/flats keep one flange facing a
  // consistent direction (tower-inward) instead of spinning frame to frame.
  private rollAngle: number[];
  // Guys: one drooping catenary each, drawn as a chain of GUY_SEGMENTS thin
  // cylinders in a shared InstancedMesh. guyList holds their member indices;
  // guySlot maps member index → its position in guyList (−1 if not a guy).
  private guyMesh?: THREE.InstancedMesh;
  private guyList: number[] = [];
  private guySlot: number[];
  private latticeVisible: boolean; // false under a monopole tube skin
  // Failure debris: one InstancedMesh PER shape family (pipe/angle/flat/rod), so a
  // broken member's wreckage keeps its true cross-section (an angle stays an L, a
  // flat bar stays flat, a rod/pipe stays round) instead of turning back into a
  // plain tube. Each family mesh holds two shard slots per member of that family,
  // at local slots 2*shapeSlot[idx] and 2*shapeSlot[idx]+1. A buckled member draws
  // both shards as a bent/kinked column; a snapped member draws both as torn stub
  // halves pulled apart. Live members park both shards squashed away. Guys
  // (tension-only) have no shape family and emit no shape debris.
  private debrisMeshes: Record<ShapeKind, THREE.InstancedMesh>;
  private joints: THREE.InstancedMesh;
  // Bolted-steelwork hardware at connections (static, follow node positions like
  // joints). jointPlate/jointBolts decorate interior ring nodes; basePlate/
  // baseBolts decorate the fixed base nodes (tower-to-foundation). All instanced.
  private jointPlate?: THREE.InstancedMesh;
  private jointBolts?: THREE.InstancedMesh;
  private jointNodeIdx: number[] = []; // ring node indices that get a joint plate
  private basePlate?: THREE.InstancedMesh;
  private baseBolts?: THREE.InstancedMesh;
  private baseNodeIdx: number[] = []; // base node indices that get a base plate
  private static readonly BASE_BOLTS = 6; // anchor bolts per base plate
  private static readonly JOINT_BOLTS = 4; // bolt studs per interior gusset plate
  private tube?: THREE.InstancedMesh;
  private tubePanelMembers: number[][] = []; // member indices per tube panel
  private tubeBase = cSteel; // realistic-view tube colour (dominant material)

  // Ice coating: one translucent frosted sleeve over every non-failed member and
  // over each drooping guy segment, sized larger than the steel so it reads as a
  // frosted coat. Hidden entirely (visible=false, zero cost) when iceThickness=0.
  // One straight sleeve per member at slot `idx`; each guy's GUY_SEGMENTS ice
  // sleeves live at iceGuyBase + guySlot*GUY_SEGMENTS + segment.
  private iceMesh: THREE.InstancedMesh;
  private iceGuyBase: number; // first ice slot used by guy segments
  private iceThickness = 0; // radial ice in metres (0 = none)

  // STRESS view (default): members colored by utilization (green→red). REALISTIC
  // view: members colored as galvanised steel. Toggled via setRealistic().
  private realistic = false;

  private radii: number[];
  // Per-member break parameters (lazily filled the first time a member fails):
  // which endpoint the stub hangs off (0=i, 1=j), stub length fraction, and how
  // far it droops toward gravity. Stable across frames so debris doesn't jitter.
  private breakSeed: (BreakParams | undefined)[];
  private dummy = new THREE.Object3D();

  // Omnitouch flag flying from the top of the tower, waving in the breeze.
  private flagGroup?: THREE.Group;
  private flagMesh?: THREE.Mesh;
  private flagTex?: THREE.CanvasTexture;
  private flagW = 3;
  private flagTime = 0;
  private topNodeIdx = -1;
  private windSpeed = 0; // m/s — drives the cloth (wind force + flutter)
  private windDir = 0; // radians — flag streams this way
  // Verlet cloth: particles pinned at the pole (ix=0), inextensible via constraints.
  private static readonly FLAG_NX = 16;
  private static readonly FLAG_NY = 8;
  private clothPos?: Float32Array; // live particle positions (== geometry attr array)
  private clothPrev?: Float32Array; // previous positions (Verlet)
  private clothRest?: Float32Array; // rest positions (pinned column follows these)
  private clothPin?: Uint8Array;
  private clothCons?: Array<[number, number, number]>; // [a, b, restLength]

  /** Feed the current wind so the flag streams downwind and only flies when it blows. */
  setWind(speed: number, dir: number): void {
    this.windSpeed = speed;
    this.windDir = dir;
  }
  private a = new THREE.Vector3();
  private b = new THREE.Vector3();
  private dir = new THREE.Vector3();
  private mid = new THREE.Vector3();
  private q = new THREE.Quaternion();
  private cA = new THREE.Vector3();
  private cB = new THREE.Vector3();
  private drooped = new THREE.Vector3();
  // extra scratch for failure geometry (kink point, shard endpoints, side axis)
  private kink = new THREE.Vector3();
  private p0 = new THREE.Vector3();
  private p1 = new THREE.Vector3();
  private side = new THREE.Vector3();
  private qroll = new THREE.Quaternion();
  // catenary scratch: successive sample points along a drooping guy
  private catPrev = new THREE.Vector3();
  private catCur = new THREE.Vector3();
  // over-stretch clamp scratch (parted-connection guard)
  private clampA = new THREE.Vector3();
  private clampB = new THREE.Vector3();

  private foundation!: BuiltTower["foundation"];
  constructor(built: BuiltTower) {
    this.structure = built.structure;
    this.render = built.render;
    this.foundation = built.foundation;
    const isTube = this.render.skin === "tube";
    this.latticeVisible = !isTube;

    const members = this.structure.members;
    this.radii = members.map(visualRadius);
    this.breakSeed = new Array(members.length).fill(undefined);

    // --- pick proxy (invisible; instanceId === member index) ---
    // One straight unit-cylinder per member, sized to that member so its raycast
    // footprint matches the visible shape. material.visible=false keeps it out of
    // the render pass but still intersectable by the raycaster used in main.ts.
    const pGeo = new THREE.CylinderGeometry(1, 1, 1, 6);
    const pMat = new THREE.MeshBasicMaterial({ visible: false });
    this.pickProxy = new THREE.InstancedMesh(pGeo, pMat, members.length);
    this.pickProxy.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.pickProxy.frustumCulled = false; // always raycastable even off-screen
    this.group.add(this.pickProxy);

    // --- classify members into shape families + assign local slots ---
    this.shapeOf = new Array(members.length).fill(null);
    this.shapeSlot = new Array(members.length).fill(-1);
    this.guySlot = new Array(members.length).fill(-1);
    this.rollAngle = new Array(members.length).fill(0);
    const counts: Record<ShapeKind, number> = { pipe: 0, angle: 0, flat: 0, rod: 0 };
    members.forEach((m, idx) => {
      if (m.tensionOnly) {
        this.guySlot[idx] = this.guyList.length;
        this.guyList.push(idx);
        return;
      }
      const kind = classifyShape(m);
      this.shapeOf[idx] = kind;
      this.shapeSlot[idx] = counts[kind]++;
      // Stable roll: face one flange toward the tower axis (x=z=0). Precomputed
      // once from the rest geometry so it never spins under load.
      const na = this.structure.nodes[m.i]!;
      const nb = this.structure.nodes[m.j]!;
      this.rollAngle[idx] = Math.atan2((na.z + nb.z) * 0.5, (na.x + nb.x) * 0.5);
    });

    // --- one visible InstancedMesh per shape family ---
    const mMat = new THREE.MeshStandardMaterial({ metalness: 0.35, roughness: 0.5 });
    const makeFamily = (kind: ShapeKind): THREE.InstancedMesh => {
      const mesh = new THREE.InstancedMesh(makeShapeGeometry(kind), mMat.clone(), Math.max(1, counts[kind]));
      mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      mesh.setColorAt(0, cGreen);
      mesh.visible = !isTube && counts[kind] > 0; // hide internal lattice under a tube skin
      this.group.add(mesh);
      return mesh;
    };
    this.shapeMeshes = {
      pipe: makeFamily("pipe"),
      angle: makeFamily("angle"),
      flat: makeFamily("flat"),
      rod: makeFamily("rod"),
    };
    mMat.dispose(); // template only; each family got its own clone



    // --- guy cables (drooping catenary, GUY_SEGMENTS thin cylinders each) ---
    if (this.guyList.length > 0) {
      const gGeo = new THREE.CylinderGeometry(1, 1, 1, 5);
      const gMat = new THREE.MeshStandardMaterial({ metalness: 0.3, roughness: 0.6 });
      this.guyMesh = new THREE.InstancedMesh(gGeo, gMat, this.guyList.length * GUY_SEGMENTS);
      this.guyMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      this.guyMesh.castShadow = true;
      this.guyMesh.setColorAt(0, cGreen);
      this.guyMesh.visible = !isTube;
      this.group.add(this.guyMesh);
    }

    // --- ice coating (frosted translucent sleeves; hidden unless ice > 0) ---
    // One sleeve per member (straight over the member) plus GUY_SEGMENTS sleeves
    // per guy (following its catenary). Sized once here; matrices are only written
    // in update() when iceThickness > 0. Starts hidden for zero cost with no ice.
    this.iceGuyBase = members.length;
    const iceCount = members.length + this.guyList.length * GUY_SEGMENTS;
    const iceGeo = new THREE.CylinderGeometry(1, 1, 1, 8);
    const iceMat = new THREE.MeshStandardMaterial({
      color: cIce,
      metalness: 0.0,
      roughness: 0.2,
      transparent: true,
      opacity: 0.55,
    });
    this.iceMesh = new THREE.InstancedMesh(iceGeo, iceMat, Math.max(1, iceCount));
    this.iceMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.iceMesh.frustumCulled = false;
    this.iceMesh.visible = false; // no ice until setIce(>0)
    this.group.add(this.iceMesh);

    // --- failure debris (one InstancedMesh per shape family, two shards/member) ---
    // Each family's wreckage reuses the SAME cross-section geometry as its live
    // members (makeShapeGeometry) so a broken angle stays an L, a flat bar stays
    // flat, a rod/pipe stays round. Two shard slots per member of that family.
    const dMat = new THREE.MeshStandardMaterial({ metalness: 0.45, roughness: 0.85 });
    this.dummy.position.set(0, 0, 0);
    this.dummy.quaternion.identity();
    this.dummy.scale.setScalar(HIDDEN_SCALE);
    this.dummy.updateMatrix();
    const makeDebris = (kind: ShapeKind): THREE.InstancedMesh => {
      const dCount = Math.max(1, counts[kind] * 2);
      const mesh = new THREE.InstancedMesh(makeShapeGeometry(kind), dMat.clone(), dCount);
      mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      mesh.setColorAt(0, cChar);
      mesh.visible = !isTube; // debris belongs to the lattice; tube has its own break logic
      // Start every shard parked (hidden) so untouched slots never show.
      for (let s = 0; s < dCount; s++) mesh.setMatrixAt(s, this.dummy.matrix);
      mesh.instanceMatrix.needsUpdate = true;
      this.group.add(mesh);
      return mesh;
    };
    this.debrisMeshes = {
      pipe: makeDebris("pipe"),
      angle: makeDebris("angle"),
      flat: makeDebris("flat"),
      rod: makeDebris("rod"),
    };
    dMat.dispose(); // template only; each family got its own clone

    // --- joints ---
    // The dark spheres at every node read as "black bubbles" in the 3D view, so
    // keep them hidden; the members (and the bolted gusset hardware) already show
    // where things connect. The mesh is still built so update()/dispose() stay simple.
    const jGeo = new THREE.SphereGeometry(0.09, 8, 6);
    const jMat = new THREE.MeshStandardMaterial({ color: 0x2a323c, metalness: 0.4, roughness: 0.6 });
    this.joints = new THREE.InstancedMesh(jGeo, jMat, this.structure.nodes.length);
    this.joints.visible = false;
    this.group.add(this.joints);

    // --- bolted-steelwork hardware (gusset plates + bolts at connections) ---
    this.buildBoltwork(isTube);

    // --- tube skin (monopole) ---
    if (isTube) this.buildTube();

    // --- static foundations + anchors ---
    this.buildStaticBlocks();
    this.buildFlag();

    this.update();
  }

  /** An Omnitouch flag flying from the top of the tower (orange, waving). */
  private buildFlag(): void {
    const nodes = this.structure.nodes;
    let top = 0;
    for (let i = 1; i < nodes.length; i++) if (nodes[i]!.y > nodes[top]!.y) top = i;
    this.topNodeIdx = top;
    const baseY = this.render.baseNodes.length ? nodes[this.render.baseNodes[0]!]!.y : 0;
    const height = Math.max(6, nodes[top]!.y - baseY);
    const W = (this.flagW = Math.max(2.6, height * 0.07));
    const H = W * 0.5; // the logo is wide → a wide flag
    const poleH = H * 2.0;

    const g = new THREE.Group();
    const poleGeo = new THREE.CylinderGeometry(W * 0.016, W * 0.016, poleH, 8);
    poleGeo.translate(0, poleH / 2, 0);
    g.add(new THREE.Mesh(poleGeo, new THREE.MeshStandardMaterial({ color: 0x9aa0a6, metalness: 0.6, roughness: 0.4 })));

    // --- cloth grid: NX along the flag, NY vertical; pinned at the pole (ix = 0) ---
    const NX = StructureView.FLAG_NX;
    const NY = StructureView.FLAG_NY;
    const N = NX * NY;
    const pos = new Float32Array(N * 3);
    const rest = new Float32Array(N * 3);
    const uv = new Float32Array(N * 2);
    const pin = new Uint8Array(N);
    for (let iy = 0; iy < NY; iy++) {
      for (let ix = 0; ix < NX; ix++) {
        const k = iy * NX + ix;
        const x = (ix / (NX - 1)) * W;
        const y = poleH - (iy / (NY - 1)) * H; // iy = 0 at the top of the pole
        pos[k * 3] = rest[k * 3] = x;
        pos[k * 3 + 1] = rest[k * 3 + 1] = y;
        pos[k * 3 + 2] = rest[k * 3 + 2] = 0;
        uv[k * 2] = ix / (NX - 1);
        uv[k * 2 + 1] = 1 - iy / (NY - 1);
        if (ix === 0) pin[k] = 1; // hoist edge on the pole
      }
    }
    const idx: number[] = [];
    for (let iy = 0; iy < NY - 1; iy++) {
      for (let ix = 0; ix < NX - 1; ix++) {
        const a = iy * NX + ix;
        idx.push(a, a + NX, a + 1, a + 1, a + NX, a + NX + 1);
      }
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.BufferAttribute(pos, 3));
    geo.setAttribute("uv", new THREE.BufferAttribute(uv, 2));
    geo.setIndex(idx);
    geo.computeVertexNormals();

    // constraints: structural (neighbours) + shear (diagonals) → inextensible cloth
    const cons: Array<[number, number, number]> = [];
    const link = (a: number, b: number) => {
      const dx = pos[a * 3]! - pos[b * 3]!;
      const dy = pos[a * 3 + 1]! - pos[b * 3 + 1]!;
      const dz = pos[a * 3 + 2]! - pos[b * 3 + 2]!;
      cons.push([a, b, Math.hypot(dx, dy, dz)]);
    };
    for (let iy = 0; iy < NY; iy++) {
      for (let ix = 0; ix < NX; ix++) {
        const k = iy * NX + ix;
        if (ix < NX - 1) link(k, k + 1);
        if (iy < NY - 1) link(k, k + NX);
        if (ix < NX - 1 && iy < NY - 1) {
          link(k, k + NX + 1);
          link(k + 1, k + NX);
        }
      }
    }

    this.flagTex = makeFlagTexture();
    const flag = new THREE.Mesh(
      geo,
      new THREE.MeshStandardMaterial({ map: this.flagTex, side: THREE.DoubleSide, roughness: 0.85 }),
    );
    flag.castShadow = true;
    g.add(flag);

    this.clothPos = pos;
    this.clothPrev = pos.slice();
    this.clothRest = rest;
    this.clothPin = pin;
    this.clothCons = cons;
    this.flagMesh = flag;
    this.flagGroup = g;
    this.group.add(g);
  }

  /** Verlet cloth step: gravity + wind + turbulence, then constraint relaxation, so
   *  the flag hangs limp when calm and streams/ripples downwind when it blows. */
  private updateFlag(): void {
    const g = this.flagGroup;
    const flag = this.flagMesh;
    const pos = this.clothPos;
    const prev = this.clothPrev;
    const rest = this.clothRest;
    const pin = this.clothPin;
    const cons = this.clothCons;
    if (!g || !flag || !pos || !prev || !rest || !pin || !cons || this.topNodeIdx < 0) return;

    const t = this.structure.nodes[this.topNodeIdx]!;
    g.position.set(t.x, t.y, t.z);
    g.rotation.y = -this.windDir; // sim runs in local space; +X = downwind, +Y = world-up

    const dt2 = (1 / 60) * (1 / 60);
    const damp = 0.986;
    const G = 9.0; // gravity (local −Y) → the droop
    const wf = Math.max(0, Math.min(1.4, this.windSpeed / 16));
    const windA = wf * 22; // steady downwind push (local +X)
    this.flagTime += 1 / 60;
    const tm = this.flagTime;
    const N = pos.length / 3;

    for (let k = 0; k < N; k++) {
      const i = k * 3;
      if (pin[k]) {
        // hoist edge stays on the pole (the group carries it to the tower top)
        pos[i] = prev[i] = rest[i]!;
        pos[i + 1] = prev[i + 1] = rest[i + 1]!;
        pos[i + 2] = prev[i + 2] = rest[i + 2]!;
        continue;
      }
      const frac = rest[i]! / this.flagW; // 0 at pole → 1 at the free edge
      const ph = tm * 9 + rest[i]! * 3 + rest[i + 1]!;
      // accelerations: gravity + downwind push + turbulent flap (only with wind)
      const ax = windA;
      const ay = -G + Math.sin(ph * 0.8) * windA * 0.28 * frac;
      const az = Math.sin(ph) * windA * 0.55 * frac;
      const vx = (pos[i]! - prev[i]!) * damp;
      const vy = (pos[i + 1]! - prev[i + 1]!) * damp;
      const vz = (pos[i + 2]! - prev[i + 2]!) * damp;
      prev[i] = pos[i]!;
      prev[i + 1] = pos[i + 1]!;
      prev[i + 2] = pos[i + 2]!;
      pos[i] = pos[i]! + vx + ax * dt2;
      pos[i + 1] = pos[i + 1]! + vy + ay * dt2;
      pos[i + 2] = pos[i + 2]! + vz + az * dt2;
    }

    for (let iter = 0; iter < 6; iter++) {
      for (const [a, b, rl] of cons) {
        if (pin[a] && pin[b]) continue;
        const ai = a * 3;
        const bi = b * 3;
        const dx = pos[bi]! - pos[ai]!;
        const dy = pos[bi + 1]! - pos[ai + 1]!;
        const dz = pos[bi + 2]! - pos[ai + 2]!;
        const d = Math.hypot(dx, dy, dz) || 1e-6;
        const diff = (d - rl) / d;
        const wA = pin[a] ? 0 : pin[b] ? 1 : 0.5;
        const wB = pin[b] ? 0 : pin[a] ? 1 : 0.5;
        pos[ai] = pos[ai]! + dx * diff * wA;
        pos[ai + 1] = pos[ai + 1]! + dy * diff * wA;
        pos[ai + 2] = pos[ai + 2]! + dz * diff * wA;
        pos[bi] = pos[bi]! - dx * diff * wB;
        pos[bi + 1] = pos[bi + 1]! - dy * diff * wB;
        pos[bi + 2] = pos[bi + 2]! - dz * diff * wB;
      }
    }

    const attr = flag.geometry.attributes.position!;
    attr.needsUpdate = true; // clothPos IS the geometry array
    flag.geometry.computeVertexNormals();
  }

  private buildTube(): void {
    const rings = this.render.rings;
    const panels = rings.length - 1;
    const tGeo = new THREE.CylinderGeometry(1, 1, 1, 16, 1, true);
    const tMat = new THREE.MeshStandardMaterial({
      metalness: 0.6,
      roughness: 0.4,
      side: THREE.DoubleSide,
    });
    this.tube = new THREE.InstancedMesh(tGeo, tMat, panels);
    this.tube.castShadow = true;
    this.tube.receiveShadow = true;
    // realistic tube colour = the most common structural (non-cable) material
    const tally = new Map<string, { n: number; m: Member }>();
    for (const m of this.structure.members) {
      if (m.tensionOnly) continue;
      const e = tally.get(m.material.name);
      if (e) e.n++;
      else tally.set(m.material.name, { n: 1, m });
    }
    let best: { n: number; m: Member } | undefined;
    for (const e of tally.values()) if (!best || e.n > best.n) best = e;
    this.tubeBase = best ? materialBase(best.m).clone() : cSteel;
    this.tube.setColorAt(0, cSteel);
    this.group.add(this.tube);

    // map which members belong to each tube panel (endpoints in consecutive rings)
    const level = new Int32Array(this.structure.nodes.length).fill(-1);
    rings.forEach((row, lvl) => row.forEach((idx) => (level[idx] = lvl)));
    this.tubePanelMembers = Array.from({ length: panels }, () => []);
    this.structure.members.forEach((m, mi) => {
      const li = level[m.i]!;
      const lj = level[m.j]!;
      if (li < 0 || lj < 0) return;
      const p = Math.min(li, lj);
      if (Math.abs(li - lj) <= 1 && p < panels) this.tubePanelMembers[p]!.push(mi);
    });
  }

  private buildStaticBlocks(): void {
    const nodes = this.structure.nodes;
    const f = this.foundation;
    // concrete: a pedestal ABOVE grade (so the pad reads "on the surface") plus the
    // real buried block down to `depth` — sized to the actual footing/pier spec.
    const PEDESTAL = 0.18;
    const placeFootings = (
      indices: number[],
      spec: { type: string; width: number; diameter: number; depth: number },
    ) => {
      if (!indices.length) return;
      const isPier = spec.type === "pier" || spec.type === "rock-anchor";
      const w = isPier ? Math.max(0.3, spec.diameter) : Math.max(0.5, spec.width);
      const depth = Math.max(0.4, spec.depth);
      const totalH = depth + PEDESTAL;
      const geo = isPier
        ? new THREE.CylinderGeometry(w / 2, w / 2, totalH, 20)
        : new THREE.BoxGeometry(w, totalH, w);
      const mat = new THREE.MeshStandardMaterial({ color: 0xc0bbb0, metalness: 0.02, roughness: 0.96 });
      const mesh = new THREE.InstancedMesh(geo, mat, indices.length);
      mesh.receiveShadow = true;
      mesh.castShadow = true;
      indices.forEach((idx, k) => {
        const n = nodes[idx]!;
        // top at grade + pedestal, bottom at grade − depth → centre height:
        this.dummy.position.set(n.x, n.y + PEDESTAL - totalH / 2, n.z);
        this.dummy.rotation.set(0, 0, 0);
        this.dummy.scale.set(1, 1, 1);
        this.dummy.updateMatrix();
        mesh.setMatrixAt(k, this.dummy.matrix);
      });
      mesh.instanceMatrix.needsUpdate = true;
      this.group.add(mesh);
    };
    placeFootings(this.render.baseNodes, f.base); // tower base pad / pier
    placeFootings(this.render.anchors, f.anchor ?? { type: "deadman", width: 1.2, diameter: 0.6, depth: 1.5 });
  }

  /**
   * Build the bolted-steelwork hardware so joints read as bolted connections
   * rather than magic welds:
   *   • INTERIOR ring nodes (where legs/bracing meet) get a small flat gusset
   *     plate + a ring of bolt-head studs.
   *   • BASE nodes (tower-to-foundation) get a larger base plate + anchor bolts.
   * Everything is instanced dark galvanised steel. For very large towers only a
   * representative subset of interior nodes is decorated (capped) so the bolt/
   * plate instance count stays cheap. Positions follow the nodes each update()
   * (updateBoltwork), so plates stay attached as the tower deforms.
   * @param isTube monopole tube skin hides the internal lattice → skip the
   *        interior joint plates (nothing to bolt visibly), but still bolt the base.
   */
  private buildBoltwork(isTube: boolean): void {
    const nodes = this.structure.nodes;
    const baseSet = new Set(this.render.baseNodes);

    // --- base plate + anchor bolts (tower-to-foundation) ---
    this.baseNodeIdx = this.render.baseNodes.filter((idx) => nodes[idx] !== undefined);
    const nBase = this.baseNodeIdx.length;
    if (nBase > 0) {
      const bpGeo = new THREE.BoxGeometry(0.62, 0.07, 0.62); // wide, thin steel plate
      const bpMat = new THREE.MeshStandardMaterial({ color: cGalv, metalness: 0.55, roughness: 0.55 });
      this.basePlate = new THREE.InstancedMesh(bpGeo, bpMat, nBase);
      this.basePlate.castShadow = true;
      this.basePlate.receiveShadow = true;
      this.group.add(this.basePlate);

      const abGeo = new THREE.CylinderGeometry(0.035, 0.035, 0.14, 6); // anchor bolt stud
      const abMat = new THREE.MeshStandardMaterial({ color: cGalv, metalness: 0.6, roughness: 0.5 });
      this.baseBolts = new THREE.InstancedMesh(abGeo, abMat, nBase * StructureView.BASE_BOLTS);
      this.baseBolts.castShadow = true;
      this.group.add(this.baseBolts);
    }

    // --- interior joint gusset plates + bolt studs (lattice only) ---
    if (!isTube) {
      // Decorate interior (non-base, non-anchor, non-fixed) ring nodes. Cap the
      // count so large towers stay cheap; sample evenly across the list.
      const anchorSet = new Set(this.render.anchors);
      const candidates: number[] = [];
      for (const row of this.render.rings) {
        for (const idx of row) {
          const n = nodes[idx];
          if (!n || n.fixed || baseSet.has(idx) || anchorSet.has(idx)) continue;
          candidates.push(idx);
        }
      }
      const CAP = 220; // max interior joints to decorate (perf ceiling)
      if (candidates.length <= CAP) {
        this.jointNodeIdx = candidates;
      } else {
        const step = candidates.length / CAP;
        this.jointNodeIdx = [];
        for (let k = 0; k < CAP; k++) this.jointNodeIdx.push(candidates[Math.floor(k * step)]!);
      }

      const nJoint = this.jointNodeIdx.length;
      if (nJoint > 0) {
        const jpGeo = new THREE.BoxGeometry(0.26, 0.05, 0.26); // small gusset plate
        const jpMat = new THREE.MeshStandardMaterial({ color: cGalv, metalness: 0.5, roughness: 0.55 });
        this.jointPlate = new THREE.InstancedMesh(jpGeo, jpMat, nJoint);
        this.jointPlate.castShadow = true;
        this.jointPlate.receiveShadow = true;
        this.jointPlate.visible = !isTube;
        this.group.add(this.jointPlate);

        const jbGeo = new THREE.CylinderGeometry(0.022, 0.022, 0.07, 6); // bolt-head stud
        const jbMat = new THREE.MeshStandardMaterial({ color: cGalv, metalness: 0.6, roughness: 0.5 });
        this.jointBolts = new THREE.InstancedMesh(jbGeo, jbMat, nJoint * StructureView.JOINT_BOLTS);
        this.jointBolts.castShadow = true;
        this.jointBolts.visible = !isTube;
        this.group.add(this.jointBolts);
      }
    }
  }

  // Position the bolt plates + bolt studs at their node positions. Called every
  // update() (after node positions are refreshed) so hardware tracks the moving
  // joints just like the sphere joints do. No per-frame allocation. Bolts are
  // laid out in a ring in the local X/Z plane on top of the (horizontal) plate.
  private updateBoltwork(): void {
    const nodes = this.structure.nodes;

    if (this.basePlate && this.baseBolts) {
      const N = StructureView.BASE_BOLTS;
      const ring = 0.22; // anchor-bolt ring radius on the base plate
      for (let k = 0; k < this.baseNodeIdx.length; k++) {
        const n = nodes[this.baseNodeIdx[k]!]!;
        this.dummy.position.set(n.x, n.y + 0.035, n.z);
        this.dummy.quaternion.identity();
        this.dummy.scale.set(1, 1, 1);
        this.dummy.updateMatrix();
        this.basePlate.setMatrixAt(k, this.dummy.matrix);
        for (let b = 0; b < N; b++) {
          const ang = (b / N) * Math.PI * 2;
          this.dummy.position.set(n.x + Math.cos(ang) * ring, n.y + 0.11, n.z + Math.sin(ang) * ring);
          this.dummy.quaternion.identity();
          this.dummy.scale.set(1, 1, 1);
          this.dummy.updateMatrix();
          this.baseBolts.setMatrixAt(k * N + b, this.dummy.matrix);
        }
      }
      this.basePlate.instanceMatrix.needsUpdate = true;
      this.baseBolts.instanceMatrix.needsUpdate = true;
    }

    if (this.jointPlate && this.jointBolts) {
      const N = StructureView.JOINT_BOLTS;
      const ring = 0.085; // bolt ring radius on the small gusset plate
      for (let k = 0; k < this.jointNodeIdx.length; k++) {
        const n = nodes[this.jointNodeIdx[k]!]!;
        this.dummy.position.set(n.x, n.y, n.z);
        this.dummy.quaternion.identity();
        this.dummy.scale.set(1, 1, 1);
        this.dummy.updateMatrix();
        this.jointPlate.setMatrixAt(k, this.dummy.matrix);
        for (let b = 0; b < N; b++) {
          const ang = (b / N) * Math.PI * 2 + 0.4;
          this.dummy.position.set(n.x + Math.cos(ang) * ring, n.y + 0.045, n.z + Math.sin(ang) * ring);
          this.dummy.quaternion.identity();
          this.dummy.scale.set(1, 1, 1);
          this.dummy.updateMatrix();
          this.jointBolts.setMatrixAt(k * N + b, this.dummy.matrix);
        }
      }
      this.jointPlate.instanceMatrix.needsUpdate = true;
      this.jointBolts.instanceMatrix.needsUpdate = true;
    }
  }

  update(): void {
    const { nodes, members } = this.structure;
    const ice = this.iceThickness;
    const iceOn = ice > 0 && this.latticeVisible;
    this.iceMesh.visible = iceOn;
    this.updateFlag();

    // The pick proxy must always track the live member endpoints so raycasts in
    // main.ts resolve to the correct member index (kept in sync with the visible
    // shapes below). Updated alongside the lattice; on a tube skin the lattice is
    // hidden but the proxy still follows the (hidden) internal centerline.
    if (this.latticeVisible) {
      for (let idx = 0; idx < members.length; idx++) {
        const m = members[idx]!;
        const r = this.radii[idx]!;

        // pick proxy: straight unit-cylinder along the member (always).
        this.orient(nodes[m.i]!, nodes[m.j]!, Math.max(r, 0.05));
        this.pickProxy.setMatrixAt(idx, this.dummy.matrix);

        // guys are drawn as drooping catenary cables, not shape instances.
        if (m.tensionOnly) {
          this.drawGuy(idx, m, nodes[m.i]!, nodes[m.j]!, iceOn);
          continue;
        }

        const kind = this.shapeOf[idx]!;
        const mesh = this.shapeMeshes[kind];
        const slot = this.shapeSlot[idx]!;
        if (m.failed) {
          // The live shape is replaced by gnarly wreckage in the debris mesh:
          // buckled columns bow/kink, snapped ones tear apart. Park the shape
          // instance so the tidy straight member doesn't ghost through.
          this.hideShape(mesh, slot);
          this.drawWreck(idx, m, nodes[m.i]!, nodes[m.j]!, r);
          if (iceOn) this.hideIce(idx); // broken member: coating fell off with it
          continue;
        }
        // Over-stretch guard: a live member whose endpoints have drifted past
        // 1.6×L0 has effectively parted (physics dropped its constraint) — draw a
        // clamped span from the anchor end instead of a rubber band. clampSpan
        // loads the (possibly pulled-in) endpoints into clampA/clampB.
        this.clampSpan(nodes[m.i]!, nodes[m.j]!, m.L0);
        this.orientShapeAB(this.clampA, this.clampB, r, this.rollAngle[idx]!);
        mesh.setMatrixAt(slot, this.dummy.matrix);
        mesh.setColorAt(slot, this.colorFor(idx, m, _tmp));
        this.parkDebris(idx);

        // frosted ice sleeve straight over the member (exaggerated radius so a
        // few cm of ice reads clearly). Reuse the clamped endpoints so the sleeve
        // never stretches either. Failed members are handled above (no ice).
        if (iceOn) {
          this.a.copy(this.clampA);
          this.b.copy(this.clampB);
          this.orientAB(r + ice * ICE_EXAGGERATE);
          this.iceMesh.setMatrixAt(idx, this.dummy.matrix);
        }
      }

      this.pickProxy.instanceMatrix.needsUpdate = true;
      for (const kind of SHAPE_KINDS) {
        const mesh = this.shapeMeshes[kind];
        mesh.instanceMatrix.needsUpdate = true;
        if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
      }
      if (this.guyMesh) {
        this.guyMesh.instanceMatrix.needsUpdate = true;
        if (this.guyMesh.instanceColor) this.guyMesh.instanceColor.needsUpdate = true;
      }
      if (iceOn) this.iceMesh.instanceMatrix.needsUpdate = true;
      for (const kind of SHAPE_KINDS) {
        const dmesh = this.debrisMeshes[kind];
        dmesh.instanceMatrix.needsUpdate = true;
        if (dmesh.instanceColor) dmesh.instanceColor.needsUpdate = true;
      }

      for (let i = 0; i < nodes.length; i++) {
        const n = nodes[i]!;
        this.dummy.position.set(n.x, n.y, n.z);
        this.dummy.quaternion.identity();
        this.dummy.scale.setScalar(n.fixed ? 1.6 : 1);
        this.dummy.updateMatrix();
        this.joints.setMatrixAt(i, this.dummy.matrix);
      }
      this.joints.instanceMatrix.needsUpdate = true;
    }

    this.updateBoltwork();

    if (this.tube) this.updateTube();
  }

  /**
   * Toggle between STRESS view (default, members colored by utilization) and
   * REALISTIC view (members colored as galvanised steel). Failed/slack members
   * still read as broken/slack in both, and failure debris stays charred. The
   * change takes effect on the next update() (call update() to apply now).
   */
  setRealistic(realistic: boolean): void {
    this.realistic = realistic;
  }

  /**
   * Set the current radial ice thickness (metres) coating the structure. 0 means
   * no ice — the whole ice mesh is hidden (visible=false) on the next update() for
   * zero rendering cost. When > 0, a translucent frosted sleeve is drawn over every
   * non-failed member and guy segment, sized larger than the steel (exaggerated so
   * a few cm of ice reads clearly). Takes effect on the next update().
   */
  setIce(thicknessMeters: number): void {
    this.iceThickness = Math.max(0, thicknessMeters);
  }

  // Park a single ice sleeve slot (squashed to nothing) so it doesn't render.
  private hideIce(slot: number): void {
    this.dummy.position.set(0, 0, 0);
    this.dummy.quaternion.identity();
    this.dummy.scale.setScalar(HIDDEN_SCALE);
    this.dummy.updateMatrix();
    this.iceMesh.setMatrixAt(slot, this.dummy.matrix);
  }

  // Per-member color source: utilization ramp in STRESS view, galvanised steel
  // (with per-member variation) in REALISTIC view. Failed/slack handled the same
  // in both so broken/slack always reads correctly.
  private colorFor(idx: number, m: Member, out: THREE.Color): THREE.Color {
    return this.realistic ? steelColor(idx, m, out) : memberColor(m, out);
  }

  // Draw a guy (tension-only member) as a drooping CATENARY: a chain of thin
  // cylinder segments between its endpoints. Sag depth is small when the guy is
  // taut (low util, not slack) and large when slack / the mast is collapsing, so
  // a falling mast's guys visibly hang instead of staying rigid.
  private drawGuy(idx: number, m: Member, na: Structure["nodes"][number], nb: Structure["nodes"][number], iceOn: boolean): void {
    if (!this.guyMesh) return;
    const gslot = this.guySlot[idx]!;
    const base = gslot * GUY_SEGMENTS;
    // ice sleeves for this guy live in a dedicated block of iceMesh slots. Failed
    // guys have let go, so their coating is gone (hidden).
    const iceGuy = iceOn && !m.failed;
    const iceBase = this.iceGuyBase + gslot * GUY_SEGMENTS;
    const iceR = this.radii[idx]! + this.iceThickness * ICE_EXAGGERATE * ICE_GUY_EXTRA;

    if (m.failed) {
      // SNAPPED GUY: the far end (mast attachment) has fallen away from its fixed
      // ground anchor, so spanning both endpoints would rubber-band to ~10×. A
      // parted cable instead HANGS STRAIGHT DOWN from the anchor end (the fixed
      // node, or the higher node if neither is fixed) for a fixed, sensible
      // length — it does NOT reach toward the fallen far end. All its ice is gone.
      const anchorIsA = na.fixed !== nb.fixed ? na.fixed : na.y >= nb.y;
      const anchor = anchorIsA ? na : nb;
      const hang = Math.min(m.L0 * FAILED_GUY_HANG_FRAC, FAILED_GUY_HANG_CAP);
      const r = this.radii[idx]!;
      this.colorFor(idx, m, _tmp);
      this.p0.set(anchor.x, anchor.y, anchor.z); // fixed top of the drop
      this.catPrev.copy(this.p0);
      for (let sgi = 1; sgi <= GUY_SEGMENTS; sgi++) {
        const t = sgi / GUY_SEGMENTS;
        this.catCur.copy(this.p0);
        this.catCur.y -= hang * t; // straight vertical droop from the anchor
        this.a.copy(this.catPrev);
        this.b.copy(this.catCur);
        this.orientAB(r);
        this.guyMesh.setMatrixAt(base + sgi - 1, this.dummy.matrix);
        this.guyMesh.setColorAt(base + sgi - 1, _tmp);
        this.catPrev.copy(this.catCur);
      }
      if (iceOn) for (let sgi = 0; sgi < GUY_SEGMENTS; sgi++) this.hideIce(iceBase + sgi);
      this.parkDebris(idx);
      return;
    }

    // LIVE / SLACK GUY: normal drooping catenary between its endpoints. Guard
    // against over-stretch first: if the mast is falling and the endpoints have
    // drifted past 1.6×L0, clampSpan pulls the far end in to the anchor so the
    // cable never renders as a rubber band. clampA/clampB hold the drawn ends.
    this.clampSpan(na, nb, m.L0);
    this.p0.copy(this.clampA);
    this.p1.copy(this.clampB);
    const chord = this.p0.distanceTo(this.p1) || 1e-6;

    // Sag fraction of chord length. A taut guy barely bows; a slack guy hangs
    // deeply. util rising toward 1 also relaxes it a touch (a guy near capacity
    // has stretched). Clamped so taut guys read as near-straight.
    let sagFrac: number;
    if (m.slack) sagFrac = 0.3;
    else sagFrac = 0.012 + Math.max(0, 0.14 - m.util * 0.12); // ~0.02 taut … 0.13 loose
    const sag = chord * sagFrac;

    const r = this.radii[idx]! * (m.slack ? 1.0 : 0.85);
    this.colorFor(idx, m, _tmp);

    // Parabolic approximation of a catenary: droop is 0 at both ends and maximal
    // at midspan, applied straight down (gravity) so the cable hangs naturally.
    this.catPrev.copy(this.p0);
    for (let sgi = 1; sgi <= GUY_SEGMENTS; sgi++) {
      const t = sgi / GUY_SEGMENTS;
      this.catCur.lerpVectors(this.p0, this.p1, t);
      this.catCur.y -= sag * 4 * t * (1 - t); // parabola peaking at t=0.5
      this.clampToGround(this.catCur, r); // a sagging / fallen guy rests on grade, not under it
      // orient a segment from catPrev→catCur (both loaded into a/b scratch).
      this.a.copy(this.catPrev);
      this.b.copy(this.catCur);
      this.orientAB(r);
      const slot = base + sgi - 1;
      this.guyMesh.setMatrixAt(slot, this.dummy.matrix);
      this.guyMesh.setColorAt(slot, _tmp);
      // thicker frosted ice coat over this catenary segment (dangerous real case).
      // Reuse the a/b endpoints already set for this segment via orientAB.
      if (iceGuy) {
        this.orientAB(iceR);
        this.iceMesh.setMatrixAt(iceBase + sgi - 1, this.dummy.matrix);
      }
      this.catPrev.copy(this.catCur);
    }
    this.parkDebris(idx); // guys never emit lattice debris; keep slots parked
  }

  private updateTube(): void {
    const rings = this.render.rings;
    const radii = this.render.tubeRadius!;
    for (let p = 0; p < rings.length - 1; p++) {
      this.ringCentroid(rings[p]!, this.cA);
      this.ringCentroid(rings[p + 1]!, this.cB);
      this.dir.subVectors(this.cB, this.cA);
      const len = this.dir.length() || 1e-6;
      this.mid.copy(this.cA).addScaledVector(this.dir, 0.5);
      this.q.setFromUnitVectors(UP, this.dir.multiplyScalar(1 / len));
      const r = (radii[p]! + radii[p + 1]!) * 0.5;

      // panel failed → tube segment breaks away
      const mem = this.tubePanelMembers[p]!;
      let failed = 0;
      let maxU = 0;
      for (const mi of mem) {
        const m = this.structure.members[mi]!;
        if (m.failed) failed++;
        else if (m.util > maxU) maxU = m.util;
      }
      const broken = mem.length > 0 && failed / mem.length > 0.4;

      this.dummy.position.copy(this.mid);
      this.dummy.quaternion.copy(this.q);
      this.dummy.scale.set(broken ? 0.0001 : r, len, broken ? 0.0001 : r);
      this.dummy.updateMatrix();
      this.tube!.setMatrixAt(p, this.dummy.matrix);
      // realistic → material colour; stress → steel grey. Both tint toward red
      // as the panel approaches capacity.
      _tmp.copy(this.realistic ? this.tubeBase : cSteel).lerp(cRed, Math.min(1, maxU) * 0.85);
      this.tube!.setColorAt(p, _tmp);
    }
    this.tube!.instanceMatrix.needsUpdate = true;
    if (this.tube!.instanceColor) this.tube!.instanceColor.needsUpdate = true;
  }

  private ringCentroid(row: number[], out: THREE.Vector3): THREE.Vector3 {
    out.set(0, 0, 0);
    const nodes = this.structure.nodes;
    for (const idx of row) {
      const n = nodes[idx]!;
      out.x += n.x;
      out.y += n.y;
      out.z += n.z;
    }
    return out.multiplyScalar(1 / row.length);
  }

  private orient(na: Structure["nodes"][number], nb: Structure["nodes"][number], r: number): void {
    this.a.set(na.x, na.y, na.z);
    this.b.set(nb.x, nb.y, nb.z);
    this.orientAB(r);
  }

  // Over-stretch guard for spans (live members and guys). Loads na→nb into
  // this.clampA / this.clampB; if the current distance exceeds
  // OVERSTRETCH_TRIGGER × L0 the connection has effectively parted, so the far
  // (fallen) end is pulled in to OVERSTRETCH_DRAW × L0 from the anchor end. The
  // anchor is the fixed end if exactly one end is fixed, else the higher end
  // (which typically moved less). Returns the clamped span length. No allocation.
  private clampSpan(
    na: Structure["nodes"][number],
    nb: Structure["nodes"][number],
    L0: number,
  ): number {
    this.clampA.set(na.x, na.y, na.z);
    this.clampB.set(nb.x, nb.y, nb.z);
    const dist = this.clampA.distanceTo(this.clampB);
    if (dist <= L0 * OVERSTRETCH_TRIGGER || dist < 1e-6) return dist;
    // pick the anchor end: fixed wins, else the higher (less-fallen) node.
    const anchorIsA = na.fixed !== nb.fixed ? na.fixed : na.y >= nb.y;
    const draw = L0 * OVERSTRETCH_DRAW;
    if (anchorIsA) {
      this.clampB.sub(this.clampA).multiplyScalar(draw / dist).add(this.clampA);
    } else {
      this.clampA.sub(this.clampB).multiplyScalar(draw / dist).add(this.clampB);
    }
    return draw;
  }

  // Orient the dummy as a unit-length primitive spanning this.a→this.b, scaled
  // (r, len, r). Both endpoints must already be loaded into this.a / this.b.
  private orientAB(r: number): void {
    this.dir.subVectors(this.b, this.a);
    const len = this.dir.length() || 1e-6;
    this.mid.copy(this.a).addScaledVector(this.dir, 0.5);
    this.q.setFromUnitVectors(UP, this.dir.multiplyScalar(1 / len));
    this.dummy.position.copy(this.mid);
    this.dummy.quaternion.copy(this.q);
    this.dummy.scale.set(r, len, r);
    this.dummy.updateMatrix();
  }

  // Orient the dummy as a rolled cross-section spanning explicit endpoint vectors
  // pa→pb (e.g. clamped spans), rolling by `roll` about the member axis so
  // angles/flats keep a stable orientation (one flange facing tower-inward).
  private orientShapeAB(pa: THREE.Vector3, pb: THREE.Vector3, r: number, roll: number): void {
    this.a.copy(pa);
    this.b.copy(pb);
    this.dir.subVectors(this.b, this.a);
    const len = this.dir.length() || 1e-6;
    this.mid.copy(this.a).addScaledVector(this.dir, 0.5);
    this.q.setFromUnitVectors(UP, this.dir.multiplyScalar(1 / len));
    this.qroll.setFromAxisAngle(UP, roll);
    this.q.multiply(this.qroll);
    this.dummy.position.copy(this.mid);
    this.dummy.quaternion.copy(this.q);
    this.dummy.scale.set(r, len, r);
    this.dummy.updateMatrix();
  }

  // Deterministic per-member break parameters (seeded from the member index, no
  // Math.random) so wreckage is stable frame to frame and doesn't jitter.
  private breakParams(idx: number): BreakParams {
    let bp = this.breakSeed[idx];
    if (bp) return bp;
    const rnd = (salt: number): number => {
      const h = Math.sin(idx * 12.9898 + salt * 78.233) * 43758.5453;
      return h - Math.floor(h); // 0..1
    };
    const r0 = rnd(0);
    const r1 = rnd(1);
    const r2 = rnd(2);
    const r3 = rnd(3);
    const r4 = rnd(4);
    // sideways bow direction: an arbitrary but stable unit vector, biased away
    // from vertical so a buckle visibly kinks sideways rather than along-axis.
    const ang = r0 * Math.PI * 2;
    this.side.set(Math.cos(ang), (r1 - 0.5) * 0.6, Math.sin(ang)).normalize();
    const tint = new THREE.Color().copy(cChar).lerp(cEmber, r2 * r2); // mostly charred, some embers
    bp = {
      end: r3 < 0.5 ? 0 : 1,
      frac: 0.62 + r4 * 0.16, // 62%–78% of original length survives as shards
      droop: 0.22 + r1 * 0.33, // sag toward gravity
      kinkDir: this.side.clone(),
      kinkAmt: 3.5 + r2 * 4.5, // lateral kink offset (× radius) — sharp crumple
      twist: (r0 - 0.5) * 1.4, // roll each shard off-axis so it looks mangled
      gap: 0.14 + r3 * 0.16, // snap tear gap
      tint,
    };
    this.breakSeed[idx] = bp;
    return bp;
  }

  // Park both debris shards of a live member (squashed to nothing). Shards live in
  // the member's own shape-family debris mesh at local slots 2*slot / 2*slot+1.
  // Guys (shapeOf === null) own no shape debris, so there's nothing to park.
  private parkDebris(idx: number): void {
    const kind = this.shapeOf[idx];
    if (!kind) return;
    const slot = this.shapeSlot[idx]!;
    const mesh = this.debrisMeshes[kind];
    this.dummy.position.set(0, 0, 0);
    this.dummy.quaternion.identity();
    this.dummy.scale.setScalar(HIDDEN_SCALE);
    this.dummy.updateMatrix();
    mesh.setMatrixAt(slot * 2, this.dummy.matrix);
    mesh.setMatrixAt(slot * 2 + 1, this.dummy.matrix);
  }

  // Squash a failed member's shape instance so only its debris shows.
  private hideShape(mesh: THREE.InstancedMesh, slot: number): void {
    this.dummy.position.set(0, 0, 0);
    this.dummy.quaternion.identity();
    this.dummy.scale.setScalar(HIDDEN_SCALE);
    this.dummy.updateMatrix();
    mesh.setMatrixAt(slot, this.dummy.matrix);
  }

  // Draw one debris shard spanning p0→p1, thickness r, charred `tint`, into local
  // instance `slot` of the given shape-family debris mesh. `roll` combines the
  // member's stable cross-section roll (so an L/flat shard is oriented like the
  // live member was) with a per-member mangling twist, both about the shard axis.
  private shard(
    mesh: THREE.InstancedMesh,
    slot: number,
    p0: THREE.Vector3,
    p1: THREE.Vector3,
    r: number,
    roll: number,
    tint: THREE.Color,
  ): void {
    this.dir.subVectors(p1, p0);
    const len = this.dir.length() || 1e-6;
    this.mid.copy(p0).addScaledVector(this.dir, 0.5);
    this.q.setFromUnitVectors(UP, this.dir.multiplyScalar(1 / len));
    // roll about the shard's own axis so the cross-section is oriented like the
    // live member (rollAngle) and the metal reads twisted/mangled (twist).
    this.qroll.setFromAxisAngle(UP, roll);
    this.q.multiply(this.qroll);
    this.dummy.position.copy(this.mid);
    this.dummy.quaternion.copy(this.q);
    this.dummy.scale.set(r, len, r);
    this.dummy.updateMatrix();
    mesh.setMatrixAt(slot, this.dummy.matrix);
    mesh.setColorAt(slot, tint);
  }

  /** Lift a shard point so its SURFACE rests on (or above) the terrain — debris
   *  shards droop down off their clamped end nodes and would otherwise sink through
   *  the ground. `surfaceR` keeps the shard's fat side above grade, not just its axis. */
  private clampToGround(v: THREE.Vector3, surfaceR: number): void {
    const gy = groundHeight(v.x, v.z) + surfaceR;
    if (v.y < gy) v.y = gy;
  }

  private drawWreck(idx: number, m: Member, na: Structure["nodes"][number], nb: Structure["nodes"][number], r: number): void {
    const kind = this.shapeOf[idx];
    if (!kind) return; // guys never emit shape debris (drawn as catenary)
    const mesh = this.debrisMeshes[kind];
    const slot = this.shapeSlot[idx]!;
    const s0 = slot * 2;
    const s1 = slot * 2 + 1;
    const roll = this.rollAngle[idx]!; // keep shard cross-section oriented like the live member
    const bp = this.breakParams(idx);
    this.a.set(na.x, na.y, na.z);
    this.b.set(nb.x, nb.y, nb.z);
    // guys (tension-only) always snap when they let go
    const buckle = m.failMode === "buckle" && !m.tensionOnly;

    if (buckle) {
      // BUCKLED COLUMN: still spans i→j (crushed, not severed) but kinks sharply
      // sideways at a displaced midpoint — two shards meeting in a hard V/Z bend,
      // the classic crumpled-strut look. Slightly thicker at the crush point.
      this.dir.subVectors(this.b, this.a);
      const len = this.dir.length() || 1e-6;
      this.dir.multiplyScalar(1 / len);
      // side axis orthogonal to the member: project the seed kinkDir off-axis
      this.side.copy(bp.kinkDir).addScaledVector(this.dir, -bp.kinkDir.dot(this.dir));
      if (this.side.lengthSq() < 1e-8) this.side.set(1, 0, 0).addScaledVector(this.dir, -this.dir.x);
      this.side.normalize();
      // kink point ~40% along, shoved sideways + sagged a touch under gravity
      this.kink
        .copy(this.a)
        .addScaledVector(this.dir, len * 0.42)
        .addScaledVector(this.side, r * bp.kinkAmt)
        .addScaledVector(DOWN, r * bp.kinkAmt * 0.35);
      this.clampToGround(this.kink, r * 1.15);
      this.shard(mesh, s0, this.a, this.kink, r * 1.15, roll + bp.twist, bp.tint);
      this.shard(mesh, s1, this.kink, this.b, r * 1.15, roll - bp.twist * 0.7, bp.tint);
      return;
    }

    // SNAPPED (tension fracture): torn in two, both halves pulled apart and
    // hanging/drooping off their own end node with a ragged gap between them.
    this.dir.subVectors(this.b, this.a);
    const len = this.dir.length() || 1e-6;
    this.dir.multiplyScalar(1 / len);
    this.drooped.copy(this.dir).lerp(DOWN, bp.droop);
    if (this.drooped.lengthSq() < 1e-8) this.drooped.copy(DOWN);
    this.drooped.normalize();
    // unbalanced tear: one half keeps more length than the other
    const gap = len * bp.gap;
    const total = (len - gap) * bp.frac;
    const share = bp.end === 0 ? 0.58 : 0.42;
    const lenA = total * share;
    const lenB = total * (1 - share);
    // half A hangs off node i, drooping along member dir; half B off node j,
    // drooping back the other way — leaving a torn gap in the middle.
    this.p0.copy(this.a).addScaledVector(this.drooped, lenA);
    this.clampToGround(this.p0, r * 0.85);
    this.shard(mesh, s0, this.a, this.p0, r * 0.85, roll + bp.twist, bp.tint);
    // node-j half droops toward gravity from the far end, back along -dir
    this.drooped.copy(this.dir).multiplyScalar(-1).lerp(DOWN, bp.droop);
    if (this.drooped.lengthSq() < 1e-8) this.drooped.copy(DOWN);
    this.drooped.normalize();
    this.p1.copy(this.b).addScaledVector(this.drooped, lenB);
    this.clampToGround(this.p1, r * 0.85);
    this.shard(mesh, s1, this.b, this.p1, r * 0.85, roll - bp.twist, bp.tint);
  }

  dispose(): void {
    this.group.traverse((o) => {
      if (o instanceof THREE.InstancedMesh) {
        o.geometry.dispose();
        (o.material as THREE.Material).dispose();
      }
    });
    this.group.clear();
  }
}
